import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { formatScore } from '@/lib/format';

export interface RankingBadgeProps {
  /** Composite score 0–100. */
  score: number;
  /** When true, numeral renders a coral asterisk and badge gets coral border. */
  isIncomplete?: boolean;
  /** Optional precise factor for the tooltip (D3). e.g. 0.87. */
  dogProximityFactor?: number;
  className?: string;
}

/**
 * `<RankingBadge>` — square score badge, anchor of the property card.
 *
 * Anatomy (UI-SPEC § 3.4):
 * - 96×96 (`w-24 h-24`) `rounded-2xl bg-ocean-900/80 border border-foam-50/12`
 * - Top: numeral in `font-mono text-3xl font-semibold tracking-tight`
 * - Bottom: label `of 100` in `text-xs font-semibold text-foam-50/60`
 *
 * Color rules (numeral):
 * - score ≥ 80  → `text-bronze-500` (high-fit signal — bronze RESERVED, § 1.1.1 #2)
 * - 50 ≤ s < 80 → `text-foam-50`
 * - score < 50  → `text-foam-50/70` (de-emphasised, not alarmed)
 *
 * Incomplete (D4): trailing `*` superscript in `text-coral-500` and outer
 * `border-coral-500/40`.
 *
 * Tooltip (§ 8): `Composite = static_score × dog_proximity_factor (factor: {N})`.
 */
export function RankingBadge({
  score,
  isIncomplete,
  dogProximityFactor,
  className,
}: RankingBadgeProps) {
  let numeralColor: string;
  if (score >= 80) numeralColor = 'text-bronze-500';
  else if (score >= 50) numeralColor = 'text-foam-50';
  else numeralColor = 'text-foam-50/70';

  const factorText =
    dogProximityFactor != null && Number.isFinite(dogProximityFactor)
      ? dogProximityFactor.toFixed(2)
      : '—';

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              'w-24 h-24 rounded-2xl bg-ocean-900/80 grid grid-rows-[1fr_auto] place-items-center px-4 py-3',
              isIncomplete ? 'border border-coral-500/40' : 'border border-foam-50/10',
              'cursor-help',
              className,
            )}
          >
            <span
              className={cn(
                'font-mono text-3xl font-semibold tracking-tight tabular-nums leading-none',
                numeralColor,
              )}
            >
              {formatScore(score)}
              {isIncomplete && (
                <sup className="text-coral-500 text-base font-semibold">*</sup>
              )}
            </span>
            <span className="text-xs font-semibold text-foam-50/60">of 100</span>
          </div>
        </TooltipTrigger>
        <TooltipContent
          className="bg-ocean-950/95 text-foam-50 border-foam-50/10 max-w-[320px] text-xs"
          sideOffset={6}
        >
          Composite = static_score × dog_proximity_factor (factor: {factorText})
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
